/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react'
import {useItemVariant} from '.'
import Link from '@salesforce/retail-react-app/app/components/link'
import {Text} from '@salesforce/retail-react-app/app/components/shared/ui'
import PropTypes from 'prop-types'
import {productUrlBuilder} from '../../utils/url'
import {useLocation} from 'react-router-dom'
import {sendClickEvent} from '../../utils/gtm-utils'

/**
 * In the context of a cart product item variant, this components simply renders
 * the item's name using the cart item field `productName`. We use this field
 * rather than variant `name` field as the variant detail may not yet be
 * available upon rendering.
 */
const ItemName = ({fontSize = 'sm', ...props}) => {
    const location = useLocation()
    const variant = useItemVariant()
    const productName = variant?.productName || variant?.name
    const isGiftCard = variant?.c_isGiftCard || variant?.c_isEGiftCard
    const isCheckout = /\/checkout$/.test(location?.pathname)

    // GTM Modification
    const sendGTMEvent = () => {
        sendClickEvent('cart_view_product_clicked')
    }

    return (
        <Text
            fontSize={fontSize}
            lineHeight="18px"
            display="block"
            overflow="hidden"
            textOverflow="ellipsis"
            whiteSpace="nowrap"
            width="100%"
        >
            {isGiftCard || isCheckout ? (
                productName
            ) : (
                <Link
                    {...props}
                    color="black.600"
                    to={productUrlBuilder(variant)}
                    onClick={sendGTMEvent}
                    display="block"
                    overflow="hidden"
                    textOverflow="ellipsis"
                    whiteSpace="nowrap"
                    data-testid="product-name"
                    _focusVisible={{
                        border: '3px solid',
                        borderColor: 'blue.500'
                    }}
                >
                    {productName}
                </Link>
            )}
        </Text>
    )
}

ItemName.propTypes = {
    fontSize: PropTypes.string
}

export default ItemName
